import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  InputAdornment,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useSnackbar } from 'notistack';
import Layout from '../../components/layout/Layout';
import { useNavigate } from 'react-router-dom';
import stockTransferService from '../../services/stockTransferService';
import locationService from '../../services/locationService';
import categoryService from '../../services/categoryService';
import productService from '../../services/productService';
import { getCurrentUser } from '../../services/authService';

// Styled components
const TransferButton = styled(Button)(({ theme }) => ({
  background: '#4CAF50',
  color: 'white',
  fontWeight: 'bold',
  padding: '12px 24px',
  borderRadius: '8px',
  boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
  '&:hover': {
    background: '#45a049',
    boxShadow: '0 6px 12px rgba(0,0,0,0.15)',
  },
  '&:disabled': {
    background: '#cccccc',
    color: '#666666',
  }
}));

/**
 * Create Stock Transfer Page Component
 */
const CreateStockTransferPage = () => {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  // State management
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [transferItems, setTransferItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [transferring, setTransferring] = useState(false);
  const [inStockByProduct, setInStockByProduct] = useState({});
  const [userLocation, setUserLocation] = useState(null);
  const [isLocationUser, setIsLocationUser] = useState(false);

  // Form states
  const [formData, setFormData] = useState({
    date: new Date().toISOString().slice(0, 10),
    referenceNo: '',
    fromLocation: '',
    toLocation: '',
    notes: ''
  });

  // Load initial data
  useEffect(() => {
    loadInitialData();
  }, []);

  // Filter products when search query, category, or products change
  useEffect(() => {
    filterProducts();
  }, [searchQuery, selectedCategory, products, inStockByProduct]);

  // Refresh in-stock quantities when From location changes
  useEffect(() => {
    const refreshInStock = async () => {
      try {
        if (formData.fromLocation === 'Warehouse') {
          // For warehouse, use product quantity from products table
          const map = {};
          products.forEach(p => { map[p.id] = p.quantity ?? 0; });
          setInStockByProduct(map);
        } else {
          // For location, get from location_products table
          const fromLoc = locations.find(l => l.name === formData.fromLocation);
          if (!fromLoc) { setInStockByProduct({}); return; }
          const resp = await stockTransferService.getLocationProducts(fromLoc.id);
          const map = {};
          (resp || []).forEach(item => {
            const pid = item.productId || item.product?.id;
            const qty = item.quantity || 0;
            if (pid != null) map[pid] = qty;
          });
          setInStockByProduct(map);
        }
      } catch (e) {
        console.error('Failed to load in-stock quantities:', e);
        setInStockByProduct({});
      }
    };
    if (formData.fromLocation) {
      refreshInStock();
    }
  }, [formData.fromLocation, locations, products]);

  // Keep maxQuantity on transfer items in sync
  useEffect(() => {
    if (transferItems.length === 0) return;
    setTransferItems(prev => prev.map(item => ({
      ...item,
      maxQuantity: inStockByProduct[item.productId] ?? 0
    })));
  }, [inStockByProduct]);

  const getInStock = (productId) => inStockByProduct[productId] ?? 0;

  const loadInitialData = async () => {
    let locationData = [];
    try {
      setLoading(true);
      console.log('[CreateStockTransferPage] ========== DEBUG START ==========');

      // Get current user and their location
      console.log('[CreateStockTransferPage] Step 1: Getting current user...');
      const user = getCurrentUser();
      console.log('[CreateStockTransferPage] Current user:', user);
      const userLocationData = user?.location || null;
      const hasLocation = userLocationData && userLocationData.id;
      console.log('[CreateStockTransferPage] User location from localStorage:', userLocationData, 'Has location:', hasLocation);

      // If location is missing, try to fetch from backend
      let finalUserLocation = userLocationData;
      if (!hasLocation) {
        console.log('[CreateStockTransferPage] Location not in localStorage, fetching from backend...');
        try {
          const axios = (await import('../../utils/axios')).default;
          const response = await axios.get('/auth/me');
          console.log('[CreateStockTransferPage] Backend /auth/me response:', response.data);
          if (response.data && response.data.location) {
            finalUserLocation = response.data.location;
            localStorage.setItem('user', JSON.stringify(response.data));
            console.log('[CreateStockTransferPage] ✅ Fetched location from backend:', finalUserLocation);
          }
        } catch (fetchError) {
          console.error('[CreateStockTransferPage] ❌ Could not fetch user location from backend:', fetchError);
          console.error('[CreateStockTransferPage] Error details:', fetchError.response?.data || fetchError.message);
        }
      }

      setIsLocationUser(!!finalUserLocation);
      setUserLocation(finalUserLocation);
      console.log('[CreateStockTransferPage] Final user location:', finalUserLocation, 'Is location user:', !!finalUserLocation);

      // Load locations
      console.log('[CreateStockTransferPage] Step 2: Loading locations...');
      try {
        const locationsResponse = await locationService.list({ page: 0, size: 1000 });
        console.log('[CreateStockTransferPage] Locations API response:', locationsResponse);
        locationData = Array.isArray(locationsResponse) ? locationsResponse : (locationsResponse.content || []);
        console.log('[CreateStockTransferPage] ✅ Loaded locations:', locationData.length);
        setLocations(locationData);
      } catch (error) {
        console.error('[CreateStockTransferPage] ❌ Error loading locations:', error);
        console.error('[CreateStockTransferPage] Error details:', error.response?.data || error.message);
        throw error;
      }

      // Load categories
      console.log('[CreateStockTransferPage] Step 3: Loading categories...');
      try {
        const categoriesResponse = await categoryService.getAllCategories({ page: 0, size: 1000 });
        console.log('[CreateStockTransferPage] Categories API response:', categoriesResponse);
        const categoryData = Array.isArray(categoriesResponse) ? categoriesResponse : (categoriesResponse.content || []);
        console.log('[CreateStockTransferPage] ✅ Loaded categories:', categoryData.length);
        setCategories(categoryData);
      } catch (error) {
        console.error('[CreateStockTransferPage] ❌ Error loading categories:', error);
        console.error('[CreateStockTransferPage] Error details:', error.response?.data || error.message);
        throw error;
      }

      // Load products based on user type
      if (finalUserLocation && finalUserLocation.id) {
        // Location user: Load products from location_products table
        console.log('[CreateStockTransferPage] Step 4: Loading location products for location:', finalUserLocation.name, 'ID:', finalUserLocation.id);
        try {
          const locationProducts = await stockTransferService.getLocationProducts(finalUserLocation.id);
          console.log('[CreateStockTransferPage] Location products API response:', locationProducts);
          console.log('[CreateStockTransferPage] Location products count:', locationProducts?.length || 0);

          if (!Array.isArray(locationProducts)) {
            console.error('[CreateStockTransferPage] ❌ Location products is not an array:', locationProducts);
            throw new Error('Location products API did not return an array');
          }

          const mappedProducts = (locationProducts || []).map(lp => {
            const categoryId = lp.categoryId ?? lp.product?.category?.id;
            const categoryName = lp.categoryName ?? lp.product?.category?.name;
            const mapped = {
              id: lp.productId || lp.product?.id,
              name: lp.productName || lp.product?.name,
              sku: lp.productCode || lp.product?.sku,
              category: (categoryId != null || categoryName) ? { id: categoryId, name: categoryName } : null,
              quantity: lp.quantity || 0,
              unitCost: lp.unitCost || lp.product?.unitCost || 0,
              sellingPrice: lp.sellingPrice || lp.product?.sellingPrice || 0,
              imageUrl: lp.imageUrl || lp.product?.imageUrl
            };
            return mapped;
          });
          console.log('[CreateStockTransferPage] ✅ Mapped products:', mappedProducts.length);
          console.log('[CreateStockTransferPage] Sample mapped product:', mappedProducts[0]);
          setProducts(mappedProducts);

          // Populate inStockByProduct immediately for location products
          const stockMap = {};
          mappedProducts.forEach(p => {
            if (p.id != null) {
              stockMap[p.id] = p.quantity || 0;
            }
          });
          console.log('[CreateStockTransferPage] ✅ Populated stock map with', Object.keys(stockMap).length, 'products');
          setInStockByProduct(stockMap);

          // Set fromLocation to user's location
          setFormData(prev => ({
            ...prev,
            fromLocation: finalUserLocation.name
          }));
          console.log('[CreateStockTransferPage] ✅ Set fromLocation to:', finalUserLocation.name);
        } catch (error) {
          console.error('[CreateStockTransferPage] ❌ Error loading location products:', error);
          console.error('[CreateStockTransferPage] Error details:', error.response?.data || error.message);
          console.error('[CreateStockTransferPage] Error stack:', error.stack);
          throw error;
        }
      } else {
        // Admin/Warehouse user: Load all products
        console.log('[CreateStockTransferPage] Step 4: Loading all products (admin/warehouse user)...');
        try {
          const productsResponse = await productService.getAllProducts(0, 1000);
          console.log('[CreateStockTransferPage] Products API response:', productsResponse);
          const productData = Array.isArray(productsResponse) ? productsResponse : (productsResponse.content || []);
          console.log('[CreateStockTransferPage] ✅ Loaded products:', productData.length);
          setProducts(productData);

          // Populate inStockByProduct for warehouse products
          const stockMap = {};
          productData.forEach(p => {
            if (p.id != null) {
              stockMap[p.id] = p.quantity || 0;
            }
          });
          console.log('[CreateStockTransferPage] ✅ Populated stock map with', Object.keys(stockMap).length, 'products');
          setInStockByProduct(stockMap);

          // Set fromLocation to Warehouse for admin
          setFormData(prev => ({
            ...prev,
            fromLocation: 'Warehouse'
          }));
          console.log('[CreateStockTransferPage] ✅ Set fromLocation to: Warehouse');
        } catch (error) {
          console.error('[CreateStockTransferPage] ❌ Error loading products:', error);
          console.error('[CreateStockTransferPage] Error details:', error.response?.data || error.message);
          throw error;
        }
      }

      console.log('[CreateStockTransferPage] ========== DEBUG END - SUCCESS ==========');

    } catch (error) {
      console.error('[CreateStockTransferPage] ========== DEBUG END - ERROR ==========');
      console.error('[CreateStockTransferPage] ❌❌❌ FATAL ERROR loading initial data:', error);
      console.error('[CreateStockTransferPage] Error type:', error.constructor.name);
      console.error('[CreateStockTransferPage] Error message:', error.message);
      console.error('[CreateStockTransferPage] Error response:', error.response?.data);
      console.error('[CreateStockTransferPage] Error status:', error.response?.status);
      console.error('[CreateStockTransferPage] Error stack:', error.stack);
      enqueueSnackbar(`Failed to load data: ${error.message || 'Unknown error'}`, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const filterProducts = () => {
    let filtered = products;

    // Apply search filter if provided
    if (searchQuery.trim()) {
      filtered = filtered.filter(product =>
        product.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        product.sku?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Apply category filter if selected
    if (selectedCategory) {
      const categoryIdNum = parseInt(selectedCategory, 10);
      filtered = filtered.filter(product => {
        const productCategoryId = product.category?.id;
        if (productCategoryId == null) return false;
        return Number(productCategoryId) === categoryIdNum;
      });
    }

    // Filter products that have stock (quantity > 0)
    filtered = filtered.filter(product => {
      const stock = getInStock(product.id);
      return stock > 0;
    });

    setFilteredProducts(filtered);
  };

  const handleLocationSelect = (location) => {
    setSelectedLocation(location);
    setFormData(prev => ({
      ...prev,
      toLocation: location.name
    }));
    setTransferItems([]);
  };

  const addToTransfer = (product, initialQty = 1) => {
    const maxQty = getInStock(product.id);
    if (maxQty <= 0) return;

    setTransferItems(prev => {
      const existingItem = prev.find(item => item.productId === product.id);
      if (existingItem) {
        const nextQty = Math.min(existingItem.quantity + initialQty, maxQty);
        return prev.map(item =>
          item.productId === product.id
            ? { ...item, quantity: nextQty, maxQuantity: maxQty }
            : item
        );
      } else {
        return [...prev, {
          productId: product.id,
          productName: product.name,
          productSku: product.sku,
          quantity: Math.min(initialQty, maxQty),
          maxQuantity: maxQty
        }];
      }
    });
  };

  const updateTransferQuantity = (productId, newQuantity) => {
    if (newQuantity < 0) {
      removeFromTransfer(productId);
      return;
    }

    const maxQty = getInStock(productId);

    if (newQuantity > maxQty) {
      enqueueSnackbar(`Maximum available quantity is ${maxQty}`, { variant: 'warning' });
      setTransferItems(prev => prev.map(item =>
        item.productId === productId
          ? { ...item, quantity: maxQty, maxQuantity: maxQty }
          : item
      ));
    } else {
      setTransferItems(prev => prev.map(item =>
        item.productId === productId
          ? { ...item, quantity: newQuantity, maxQuantity: maxQty }
          : item
      ));
    }
  };

  const removeFromTransfer = (productId) => {
    setTransferItems(prev => prev.filter(item => item.productId !== productId));
  };

  const handleTransfer = async () => {
    if (!selectedLocation) {
      enqueueSnackbar('Please select a To location', { variant: 'error' });
      return;
    }

    const validTransferItems = transferItems.filter(item => item.quantity > 0);

    if (validTransferItems.length === 0) {
      enqueueSnackbar('Please add products with valid quantities to transfer', { variant: 'error' });
      return;
    }

    try {
      setTransferring(true);

      const transferData = {
        locationId: selectedLocation.id,
        fromLocationId: formData.fromLocation !== 'Warehouse' ?
          locations.find(loc => loc.name === formData.fromLocation)?.id : null,
        referenceNo: formData.referenceNo,
        items: validTransferItems.map(item => ({
          productId: item.productId,
          quantity: item.quantity
        })),
        notes: formData.notes,
        transferDate: new Date(formData.date).toISOString()
      };

      await stockTransferService.transferStock(transferData);

      enqueueSnackbar('Stock transferred successfully!', { variant: 'success' });

      // Navigate back to list
      navigate('/inventory/stock-transfer');

    } catch (error) {
      console.error('Error transferring stock:', error);
      enqueueSnackbar(
        error.response?.data?.message || 'Failed to transfer stock',
        { variant: 'error' }
      );
    } finally {
      setTransferring(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Box sx={{ minHeight: '100vh', backgroundColor: '#f5f5f5', p: 3 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <IconButton onClick={() => navigate('/inventory/stock-transfer')} sx={{ mr: 2 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
            Create Stock Transfer
          </Typography>
        </Box>

        {/* Form Section */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Transfer Details
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Date"
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Reference No"
                  value={formData.referenceNo}
                  onChange={(e) => setFormData(prev => ({ ...prev, referenceNo: e.target.value }))}
                  placeholder="Enter reference number"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>Location (From)</InputLabel>
                  <Select
                    value={formData.fromLocation}
                    onChange={(e) => setFormData(prev => ({ ...prev, fromLocation: e.target.value }))}
                    label="Location (From)"
                    disabled={isLocationUser && userLocation} // Disable for location users
                  >
                    {!isLocationUser && <MenuItem value="Warehouse">Warehouse</MenuItem>}
                    {Array.isArray(locations) && locations.map((location) => (
                      <MenuItem key={location.id} value={location.name}>
                        {location.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>Location (To)</InputLabel>
                  <Select
                    value={formData.toLocation}
                    onChange={(e) => {
                      const location = locations.find(loc => loc.name === e.target.value);
                      if (location) {
                        handleLocationSelect(location);
                      }
                    }}
                    label="Location (To)"
                  >
                    <MenuItem value="">
                      <em>Please Select</em>
                    </MenuItem>
                    {!isLocationUser && <MenuItem value="Warehouse">Warehouse</MenuItem>}
                    {Array.isArray(locations) && locations
                      .filter(location => {
                        // For location users, exclude their own location from "To" dropdown
                        if (isLocationUser && userLocation && location.id === userLocation.id) {
                          return false;
                        }
                        return true;
                      })
                      .map((location) => (
                        <MenuItem key={location.id} value={location.name}>
                          {location.name}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Search Products Section */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Select Products
            </Typography>

            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  placeholder="Search products"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    label="Category"
                  >
                    <MenuItem value="">All Categories</MenuItem>
                    {Array.isArray(categories) && categories.map((category) => (
                      <MenuItem key={category.id} value={category.id}>
                        {category.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {/* Product List */}
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Product</TableCell>
                    <TableCell align="right">In Stock</TableCell>
                    <TableCell>Quantity</TableCell>
                    <TableCell>Unit Price</TableCell>
                    <TableCell>Subtotal</TableCell>
                    <TableCell>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredProducts.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          {filteredProducts.length === 0 && products.length === 0
                            ? "Loading products..."
                            : filteredProducts.length === 0
                              ? "No products found matching your criteria or no stock available"
                              : "No products to display"
                          }
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredProducts.map((product) => (
                      <TableRow key={product.id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {product.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {product.sku}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" color={getInStock(product.id) === 0 ? 'error' : 'text.primary'}>
                            {getInStock(product.id)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <IconButton
                              size="small"
                              onClick={() => updateTransferQuantity(product.id, (transferItems.find(item => item.productId === product.id)?.quantity || 0) - 1)}
                              disabled={!selectedLocation}
                            >
                              <RemoveIcon fontSize="small" />
                            </IconButton>
                            <TextField
                              size="small"
                              type="number"
                              value={transferItems.find(item => item.productId === product.id)?.quantity || ''}
                              onChange={(e) => {
                                const value = e.target.value === '' ? 0 : parseInt(e.target.value);
                                if (!isNaN(value) && value >= 0) {
                                  const existingItem = transferItems.find(item => item.productId === product.id);
                                  if (!existingItem && value > 0) {
                                    addToTransfer(product, value);
                                  } else if (existingItem) {
                                    updateTransferQuantity(product.id, value);
                                  }
                                }
                              }}
                              disabled={!selectedLocation}
                              placeholder="0"
                              inputProps={{
                                min: 0,
                                max: getInStock(product.id),
                                style: { textAlign: 'center', padding: '4px 8px' }
                              }}
                              sx={{ width: '70px' }}
                            />
                            <IconButton
                              size="small"
                              onClick={() => addToTransfer(product)}
                              disabled={!selectedLocation || getInStock(product.id) <= (transferItems.find(item => item.productId === product.id)?.quantity || 0)}
                            >
                              <AddIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            ₹{product.sellingPrice || 0}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            ₹{((transferItems.find(item => item.productId === product.id)?.quantity || 0) * (product.sellingPrice || 0)).toFixed(2)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <IconButton
                            size="small"
                            onClick={() => removeFromTransfer(product.id)}
                            color="error"
                            disabled={!transferItems.find(item => item.productId === product.id)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Total */}
            <Box sx={{ mt: 2, textAlign: 'right' }}>
              <Typography variant="h6">
                Total: ₹{transferItems.reduce((total, item) => {
                  const product = products.find(p => p.id === item.productId);
                  return total + (item.quantity * (product?.sellingPrice || 0));
                }, 0).toFixed(2)}
              </Typography>
            </Box>
          </CardContent>
        </Card>

        {/* Notes Section */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Additional Notes
            </Typography>

            <TextField
              fullWidth
              multiline
              rows={4}
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              placeholder="Enter any additional notes..."
            />
          </CardContent>
        </Card>

        {/* Transfer Button */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h5" fontWeight="bold">
            Total: ₹{transferItems.reduce((total, item) => {
              const product = products.find(p => p.id === item.productId);
              return total + (item.quantity * (product?.sellingPrice || 0));
            }, 0).toFixed(2)}
          </Typography>

          <TransferButton
            onClick={handleTransfer}
            disabled={transferring || !selectedLocation || transferItems.length === 0}
            startIcon={transferring ? <CircularProgress size={20} /> : <CheckCircleIcon />}
          >
            {transferring ? 'Transferring...' : 'Complete Transfer'}
          </TransferButton>
        </Box>
      </Box>
    </Layout>
  );
};

export default CreateStockTransferPage;

